import { DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import {
  MatChipListbox,
  MatChipListboxChange,
  MatChipOption,
} from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { identity, pickBy } from 'lodash-es';
import {
  catchError,
  combineLatest,
  debounceTime,
  EMPTY,
  map,
  switchMap,
  tap,
} from 'rxjs';
import { FileService } from '@ipg/ngx-frontend-toolset';
import { DataTableContainerComponent } from '../../components/data-table-container/data-table-container.component';
import { DialogService } from '../../components/dialog/dialog.service';
import { MapFromPipe } from '../../pipes/map-from.pipe';
import { TableLimitService } from '../../services/table-limit.service';
import { Download, DownloadType } from './downloads.model';
import { DownloadsService } from './services/downloads.service';

@Component({
  selector: 'app-downloads',
  imports: [
    MatChipListbox,
    MatChipOption,
    MatIcon,
    MatIconButton,
    MatTableModule,
    MatTooltip,
    MatPaginator,
    MatSort,
    MatSortHeader,
    NgClass,
    DatePipe,
    MapFromPipe,
    DataTableContainerComponent,
  ],
  templateUrl: './downloads.component.html',
  styleUrl: './downloads.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DownloadsComponent {
  private readonly _dialog = inject(DialogService);
  private readonly _downloads = inject(DownloadsService);
  private readonly _file = inject(FileService);
  private readonly _tableLimit = inject(TableLimitService);

  private readonly _filter = signal({
    type: '',
  });

  private readonly _query = signal({
    page: 1,
    limit: this._tableLimit.getLimit(),
  });

  private readonly _sort = signal({
    sortBy: 'created_at',
    sortOrder: 'desc',
  });

  private readonly _paginator = viewChild.required(MatPaginator);

  private readonly _params$ = combineLatest({
    query: toObservable(this._query),
    sort: toObservable(this._sort),
    filter: toObservable(this._filter),
  }).pipe(
    map(({ query, sort, filter }) => ({
      ...query,
      ...sort,
      ...pickBy(filter, identity),
    })),

    debounceTime(0)
  );

  private readonly _paginatedDownloads$ = this._params$.pipe(
    switchMap((params) => this._downloads.getAll({ params })),
    tap(({ meta }) => this._setPaginator({ page: meta.page, total: meta.total })),
    catchError(() => EMPTY)
  );

  private readonly _downloads$ = this._paginatedDownloads$.pipe(
    map(({ items }) => items)
  );

  protected readonly dataSource = toSignal(this._downloads$, { initialValue: [] });

  protected readonly hasNoData = computed(() => this.dataSource().length === 0);

  protected readonly pageSize = computed(() => this._query().limit);

  protected readonly selectedFilterType = computed(() => this._filter().type);

  protected readonly displayedColumns: (keyof Download | 'actions')[] = [
    'title',
    'createdAt',
    'type',
    'actions',
  ];

  protected readonly options = [
    {
      value: 'release_notes',
      displayValue: 'Release notes',
    },
    {
      value: 'roadmap',
      displayValue: 'Roadmaps',
    },
    {
      value: 'manual',
      displayValue: 'Handleidingen',
    },
  ];

  protected typeMapping: Record<DownloadType, string> = {
    roadmap: 'Roadmap',
    release_notes: 'Release note',
    manual: 'Handleiding',
  };

  handlePageChange({ pageIndex, pageSize }: PageEvent) {
    this._tableLimit.saveLimit(pageSize);
    this._query.update((currentState) => ({
      ...currentState,
      page: pageIndex + 1,
      limit: pageSize,
    }));
  }

  handleSortChange({ active, direction }: Sort) {
    this._sort.update((currentState) => ({
      ...currentState,
      sortBy: active,
      sortOrder: direction,
    }));
  }

  handleTypeChange({ value }: MatChipListboxChange) {
    this._filter.update((currentState) => ({
      ...currentState,
      type: value,
    }));

    this._query.update((currentState) => ({
      ...currentState,
      page: 1,
    }));
  }

  handleOpenFile(download: Download) {
    const fileUrl = this._file.get(download.fileId, true).href;
    window.open(fileUrl, 'documentViewerWindow');
  }

  private _setPaginator({ page, total }: { page: number; total: number }) {
    const paginator = this._paginator();

    paginator.pageIndex = page - 1;
    paginator.length = total;
  }
}
